import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, CardBody } from 'reactstrap';
import { type Person } from 'types';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import {
  extractPersonFilterFields,
  personFilterOnlyColumns,
  personFilterSuggestionsGenerator,
} from './personFilters';

export interface OverviewParticipant {
  person: Person;
  has_responded: boolean;
}

interface Props {
  participants: OverviewParticipant[];
  onSelectParticipant: (person: Person) => void;
}

const CustomSurveyResponsesParticipantsTab: FC<Props> = ({
  participants,
  onSelectParticipant,
}) => {
  const { formatMessage } = useIntl();

  const respondedYesLabel = formatMessage({
    id: 'app.views.administration.custom_surveys.responses.responded.yes',
    defaultMessage: 'Yes',
  });
  const respondedNoLabel = formatMessage({
    id: 'app.views.administration.custom_surveys.responses.responded.no',
    defaultMessage: 'No',
  });

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.person',
          defaultMessage: 'Person',
        }),
        field: 'person',
        isPerson: true,
        sortable: false,
        getFilterDisplayValue: (p: Person) => p.full_name,
        fieldTransformer: (value, operator) =>
          `(${operator == 'EQ' ? '==' : '!='} "${
            value.filterObject.value
          }" person.id)`,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.responded',
          defaultMessage: 'Responded',
        }),
        field: 'responded',
        className: 'col-2',
        sortable: false,
        format: (value: string) =>
          value === respondedYesLabel ? (
            value
          ) : (
            <span className="text-muted">{value}</span>
          ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'text-end text-nowrap',
        sortable: false,
        hideFromFilters: true,
      },
      // Hidden columns: extracted person attributes (manager, chain of command,
      // org attributes), used only for filtering.
      ...personFilterOnlyColumns(formatMessage),
    ],
    [formatMessage, respondedYesLabel]
  );

  const rows = useMemo(
    () =>
      participants.map((p) => ({
        ...extractPersonFilterFields(p.person),
        id: p.person.id,
        person: p.person,
        responded: p.has_responded ? respondedYesLabel : respondedNoLabel,
        actions: (
          <Button
            color="light"
            size="sm"
            disabled={!p.has_responded}
            onClick={() => onSelectParticipant(p.person)}
          >
            <FormattedMessage
              id="app.views.administration.custom_surveys.responses.view"
              defaultMessage="View"
            />
          </Button>
        ),
      })),
    [participants, onSelectParticipant, respondedYesLabel, respondedNoLabel]
  );

  if (participants.length === 0) {
    return (
      <Card>
        <CardBody className="text-muted text-center">
          <FormattedMessage
            id="app.views.administration.custom_surveys.responses.no_participants"
            defaultMessage="No participants in this instance."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <FilterablePeopleTable
      columns={columns}
      rows={rows}
      filterType={FilterType.ADVANCED}
      suggestionGenerator={personFilterSuggestionsGenerator}
      hideExportButton={true}
    />
  );
};

export default CustomSurveyResponsesParticipantsTab;
